import React, { useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';

const EmbedContainer = styled.div`
  width: 100%;
  height: 100vh;
  background: #000;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 0;
  margin: 0;
  overflow: hidden;
`;

const ComicImage = styled.img`
  max-width: 100%;
  max-height: calc(100vh - 80px);
  width: auto;
  height: auto;
  object-fit: contain;
  display: block;
`;

const Controls = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 2rem;
  padding: 1.5rem 2rem;
  background: linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%);
  border-top: 3px solid #FFD700;
  width: 100%;
  box-sizing: border-box;
`;

const NavButton = styled.button<{ disabled?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.8rem 1.5rem;
  background: ${props => props.disabled ? '#333' : 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)'};
  color: ${props => props.disabled ? '#666' : '#000'};
  border: none;
  border-radius: 25px;
  font-family: var(--font-display);
  font-size: 1rem;
  font-weight: 700;
  text-decoration: none;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.3s ease;
  pointer-events: ${props => props.disabled ? 'none' : 'auto'};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  box-shadow: ${props => props.disabled ? 'none' : '0 4px 15px rgba(255, 215, 0, 0.3)'};

  &:hover {
    transform: ${props => props.disabled ? 'none' : 'translateY(-2px)'};
    box-shadow: ${props => props.disabled ? 'none' : '0 6px 20px rgba(255, 215, 0, 0.5)'};
  }

  @media (max-width: 768px) {
    padding: 0.6rem 1rem;
    font-size: 0.9rem;
    gap: 0.3rem;
  }
`;

const PageInfo = styled.div`
  color: #FFD700;
  font-family: var(--font-display);
  font-size: 1.2rem;
  font-weight: 700;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);

  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const TOTAL_PAGES = 21;

const ComicEmbedPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const pageParam = searchParams.get('page');
  const currentPage = Math.max(1, Math.min(TOTAL_PAGES, parseInt(pageParam || '1', 10) || 1));

  // Pad page number with leading zero (01, 02, etc.)
  const paddedPage = currentPage.toString().padStart(2, '0');
  const imageUrl = `/images/comic-pages/page-${paddedPage}.jpg`;

  const prevPage = currentPage > 1 ? currentPage - 1 : null;
  const nextPage = currentPage < TOTAL_PAGES ? currentPage + 1 : null;

  // Navigation handlers
  const handlePrevious = useCallback(() => {
    if (prevPage) {
      // Use window.location for better Twitter Player Card compatibility
      window.location.href = `/comic/embed?page=${prevPage}`;
    }
  }, [prevPage]);

  const handleNext = useCallback(() => {
    if (nextPage) {
      // Use window.location for better Twitter Player Card compatibility
      window.location.href = `/comic/embed?page=${nextPage}`;
    }
  }, [nextPage]);

  useEffect(() => {
    // Preload adjacent pages for smoother navigation
    if (prevPage) {
      const img = new Image();
      img.src = `/images/comic-pages/page-${prevPage.toString().padStart(2, '0')}.jpg`;
    }
    if (nextPage) {
      const img = new Image();
      img.src = `/images/comic-pages/page-${nextPage.toString().padStart(2, '0')}.jpg`;
    }
  }, [currentPage, prevPage, nextPage]);

  return (
    <>

      <EmbedContainer>
        <ComicImage
          src={imageUrl}
          alt={`Comic page ${currentPage}`}
          loading="eager"
        />

        <Controls>
          <NavButton
            onClick={handlePrevious}
            disabled={!prevPage}
            type="button"
          >
            ◀️ Previous
          </NavButton>

          <PageInfo>
            Page {currentPage} / {TOTAL_PAGES}
          </PageInfo>

          <NavButton
            onClick={handleNext}
            disabled={!nextPage}
            type="button"
          >
            Next ▶️
          </NavButton>
        </Controls>
      </EmbedContainer>
    </>
  );
};

export default ComicEmbedPage;
